'use client';

import Image from 'next/image';

import { ArrowRight, BadgeCheck, ChevronRight, Clock, Shield, Users } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { motion, useReducedMotion } from 'motion/react';

import { UnifiedContainer } from '@/components/layout/UnifiedContainer';
import { Link } from '@/i18n/routing';
import { cn } from '@/lib/utils';
import heroPostConstruction from '@/public/images/hero-post-construction-kleaners.png';

const HERO_IMAGE_WIDTH = 1536;
const HERO_IMAGE_HEIGHT = 1024;

const TRUST_KEYS = ['insured', 'specialists', 'turnaround', 'projects'] as const;

const TRUST_ICONS = {
  insured: Shield,
  specialists: BadgeCheck,
  turnaround: Clock,
  projects: Users,
} as const;

function headlineLines(headline: string): string[] {
  return headline
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean);
}

export function PostConstructionHero() {
  const t = useTranslations('servicesPage.postConstructionLanding');
  const reducedMotion = useReducedMotion();

  const lines = headlineLines(t('headline'));
  const trustItems = TRUST_KEYS.map(key => ({
    key,
    label: t(`trust.${key}`),
    icon: TRUST_ICONS[key],
  }));

  return (
    <section className='relative isolate overflow-x-clip border-b border-[#0A2342]/8 bg-white'>
      <div
        className='pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_100%_0%,rgba(249,115,22,0.07),transparent_55%)]'
        aria-hidden
      />

      <UnifiedContainer size='xl' padding='lg' className='relative'>
        <motion.div
          className='grid grid-cols-1 items-start gap-y-6 py-8 sm:py-10 lg:grid-cols-2 lg:items-center lg:gap-10 lg:py-12 xl:gap-14 xl:py-14'
          initial={reducedMotion ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        >
          {/* Copy */}
          <div className='flex min-w-0 flex-col gap-4 lg:gap-5'>
            <span className='inline-flex w-fit items-center rounded-full border border-[#0A2342]/10 bg-[#0A2342]/[0.04] px-3 py-1 text-[0.6875rem] font-bold uppercase tracking-[0.14em] text-[#0A2342]'>
              {t('eyebrow')}
            </span>

            <h1
              id='post-construction-hero-heading'
              className='max-w-[14ch] text-pretty text-[clamp(2rem,8.5vw,2.75rem)] font-extrabold leading-[1.02] tracking-[-0.035em] text-[#0A2342] lg:max-w-[12ch] lg:text-[clamp(2.25rem,3.8vw,3.5rem)]'
            >
              {lines.map((line, index) => (
                <span
                  key={line}
                  className={cn('block', index === lines.length - 1 && 'text-[#F97316]')}
                >
                  {line}
                </span>
              ))}
            </h1>

            <p className='max-w-[46ch] text-pretty text-[0.9375rem] leading-relaxed text-[#0A2342]/65 lg:max-w-md lg:text-base'>
              {t('subtitle')}
            </p>

            <ul className='grid grid-cols-1 gap-2 sm:grid-cols-2 lg:max-w-lg'>
              {trustItems.map(item => {
                const Icon = item.icon;
                return (
                  <li
                    key={item.key}
                    className='flex items-center gap-2 text-[0.8125rem] font-semibold text-[#0A2342]/80 sm:text-sm'
                  >
                    <span
                      className='flex h-6 w-6 shrink-0 items-center justify-center rounded-xl bg-[#F97316]/10'
                      aria-hidden
                    >
                      <Icon className='h-3.5 w-3.5 text-[#F97316]' strokeWidth={2.25} />
                    </span>
                    {item.label}
                  </li>
                );
              })}
            </ul>

            <div className='flex flex-col gap-2 pt-0.5 sm:flex-row sm:flex-wrap sm:items-center lg:gap-3'>
              <Link
                href='/book?service=post_construction'
                className='inline-flex h-12 items-center justify-center gap-2 rounded-3xl bg-[#F97316] px-5 text-sm font-bold text-white shadow-[0_14px_36px_-10px_rgba(249,115,22,0.45)] transition-transform hover:bg-[#ea580c] sm:h-[3.25rem] sm:px-6 sm:text-base lg:h-14'
              >
                {t('primaryCta')}
                <ChevronRight className='h-5 w-5' aria-hidden />
              </Link>
              <Link
                href='/reviews'
                className='inline-flex h-12 items-center justify-center gap-2 rounded-3xl border-2 border-[#0A2342]/12 bg-white px-5 text-sm font-semibold text-[#0A2342] transition-colors hover:border-[#0A2342]/20 hover:bg-[#0A2342]/[0.03] sm:h-[3.25rem] sm:px-6 sm:text-base lg:h-14'
              >
                {t('secondaryCta')}
                <ArrowRight className='h-4 w-4' aria-hidden />
              </Link>
            </div>

            <p className='text-xs font-medium text-[#0A2342]/55 sm:text-sm'>
              <span className='font-bold text-[#0A2342]'>{t('socialProof.highlight')}</span>{' '}
              {t('socialProof.label')}
            </p>
          </div>

          {/* Image */}
          <div className='relative min-h-64 w-full overflow-hidden rounded-3xl sm:min-h-80 lg:min-h-[min(52vh,22rem)]'>
            <Image
              src={heroPostConstruction}
              alt={t('imageAlt')}
              width={HERO_IMAGE_WIDTH}
              height={HERO_IMAGE_HEIGHT}
              priority
              quality={90}
              sizes='(max-width: 1023px) 100vw, 46vw'
              className='h-full w-full object-cover object-[72%_28%] lg:object-[65%_22%]'
              fetchPriority='high'
            />
            <div
              className='pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0A2342]/25 via-transparent to-white/10'
              aria-hidden
            />
            <div className='absolute bottom-3 left-3 rounded-2xl border border-white/60 bg-white/90 px-2.5 py-2 shadow-lg backdrop-blur-sm sm:bottom-4 sm:left-4'>
              <p className='text-[0.625rem] font-bold uppercase tracking-[0.1em] text-[#F97316]'>
                {t('imageBadge.label')}
              </p>
              <p className='text-[0.6875rem] font-semibold text-[#0A2342] sm:text-xs'>
                {t('imageBadge.title')}
              </p>
            </div>
          </div>
        </motion.div>
      </UnifiedContainer>
    </section>
  );
}
